import React, { useEffect, useMemo, useState } from "react";
import {
  getOptionChainSnapshots,
  getStockSnapshots,
} from "../marketData/marketDataApi";
import NinaOptionsScanner from "./NinaOptionsScanner";
import "./NinaOptionsOpportunityEngine.css";

const EMPTY_SIGNATURE_OPPORTUNITIES = [];
const OPTIONS_DISCOVERY_CANDIDATES = Object.freeze([
  "SPY",
  "QQQ",
  "IWM",
  "AAPL",
  "NVDA",
  "AMD",
  "TSLA",
  "AMZN",
  "MSFT",
  "META",
  "GOOGL",
  "NFLX",
  "SMCI",
  "PLUG",
  "PCG",
  "SLV",
  "UEC",
]);
const MAX_AUTHENTICATED_OPTION_SPREAD_PERCENT = 10;
const MIN_AUTHENTICATED_OPTION_VOLUME = 100;
const MAX_AUTHENTICATED_SOURCE_AGE_MINUTES = 15;
const MAX_AUTHENTICATED_OPTION_SYMBOLS = 10;

function firstFiniteNumber(...values) {
  const value = values.find(
    (candidate) =>
      candidate !== null &&
      candidate !== undefined &&
      candidate !== "" &&
      Number.isFinite(Number(candidate)),
  );
  return value === undefined ? null : Number(value);
}

function buildOptionsDiscoveryOpportunities(payload) {
  const snapshots =
    payload?.snapshots && typeof payload.snapshots === "object"
      ? payload.snapshots
      : {};

  return Object.entries(snapshots)
    .map(([symbol, snapshot]) => {
      const price = firstFiniteNumber(
        snapshot?.latest_trade?.p,
        snapshot?.latestTrade?.p,
        snapshot?.minute_bar?.c,
        snapshot?.minuteBar?.c,
        snapshot?.daily_bar?.c,
        snapshot?.dailyBar?.c,
      );
      const previousClose = firstFiniteNumber(
        snapshot?.previous_daily_bar?.c,
        snapshot?.previousDailyBar?.c,
      );
      if (price === null || price <= 0) return null;

      const percentageChange =
        previousClose !== null && previousClose > 0
          ? ((price - previousClose) / previousClose) * 100
          : null;
      return {
        symbol: String(symbol).trim().toUpperCase(),
        price,
        previousClose,
        percentageChange,
        analysis: {
          status: "YELLOW",
          reason:
            "Dedicated options discovery candidate; direction is not confirmed.",
        },
      };
    })
    .filter(Boolean)
    .sort(
      (left, right) =>
        Math.abs(right.percentageChange || 0) -
        Math.abs(left.percentageChange || 0),
    )
    .slice(0, MAX_AUTHENTICATED_OPTION_SYMBOLS);
}

function optionRequestContext(opportunity) {
  const confirmationStatus = opportunity?.analysis?.status;
  if (confirmationStatus === "GREEN") {
    return { contractType: "call", directionConfirmed: true };
  }
  if (confirmationStatus === "RED") {
    return { contractType: "put", directionConfirmed: true };
  }

  const percentageChange = Number(opportunity?.percentageChange);
  return {
    contractType:
      Number.isFinite(percentageChange) && percentageChange < 0
        ? "put"
        : "call",
    directionConfirmed: false,
  };
}

function utcDateAfter(days) {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function NinaOptionsOpportunityEngine({
  onOpenTradeReview,
  signatureOpportunities = EMPTY_SIGNATURE_OPPORTUNITIES,
}) {
  const [discoveryOpportunities, setDiscoveryOpportunities] = useState([]);
  const [authenticatedChains, setAuthenticatedChains] = useState([]);
  const [authenticatedStatus, setAuthenticatedStatus] = useState("idle");
  const [authenticatedError, setAuthenticatedError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadOptionsDiscoveryCandidates() {
      try {
        const payload = await getStockSnapshots(
          OPTIONS_DISCOVERY_CANDIDATES,
          undefined,
          { signal: controller.signal },
        );
        if (!active) return;
        setDiscoveryOpportunities(buildOptionsDiscoveryOpportunities(payload));
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        setDiscoveryOpportunities([]);
      }
    }

    loadOptionsDiscoveryCandidates();
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const authenticatedOpportunities = useMemo(() => {
    const symbols = new Set();
    return [...signatureOpportunities, ...discoveryOpportunities]
      .filter((opportunity) => {
        const symbol = String(opportunity?.symbol || "")
          .trim()
          .toUpperCase();
        if (!symbol || symbols.has(symbol)) return false;
        symbols.add(symbol);
        return true;
      })
      .slice(0, MAX_AUTHENTICATED_OPTION_SYMBOLS);
  }, [discoveryOpportunities, signatureOpportunities]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const opportunities = authenticatedOpportunities;

    if (opportunities.length === 0) {
      setAuthenticatedChains([]);
      setAuthenticatedStatus("idle");
      setAuthenticatedError("");
      return () => {
        active = false;
        controller.abort();
      };
    }

    async function loadAuthenticatedChains() {
      setAuthenticatedStatus("loading");
      setAuthenticatedError("");
      try {
        const chains = await Promise.all(
          opportunities.map(async (opportunity) => {
            const underlyingPrice = Number(opportunity?.price);
            const requestContext = optionRequestContext(opportunity);
            if (!Number.isFinite(underlyingPrice) || underlyingPrice <= 0) {
              return {
                opportunity,
                ...requestContext,
                payload: null,
                error: "Authenticated underlying price is unavailable.",
              };
            }

            try {
              const payload = await getOptionChainSnapshots(
                opportunity.symbol,
                {
                  feed: "indicative",
                  limit: 50,
                  contractType: requestContext.contractType,
                  strikePriceGte: Number((underlyingPrice * 0.85).toFixed(2)),
                  strikePriceLte: Number((underlyingPrice * 1.15).toFixed(2)),
                  expirationDateGte: utcDateAfter(0),
                  expirationDateLte: utcDateAfter(60),
                },
                { signal: controller.signal },
              );
              return {
                opportunity,
                ...requestContext,
                payload,
                error: "",
              };
            } catch (error) {
              if (error?.name === "AbortError") throw error;
              return {
                opportunity,
                ...requestContext,
                payload: null,
                error:
                  error?.message ||
                  "Authenticated option-chain data is unavailable.",
              };
            }
          }),
        );
        if (!active) return;

        const hasAuthenticatedResponse = chains.some((chain) => chain.payload);
        const hasContracts = chains.some(
          (chain) => Object.keys(chain.payload?.snapshots || {}).length > 0,
        );
        setAuthenticatedChains(chains);
        setAuthenticatedStatus(
          hasContracts ? "ready" : hasAuthenticatedResponse ? "empty" : "error",
        );
        setAuthenticatedError(
          hasAuthenticatedResponse
            ? ""
            : "Authenticated option-chain data is unavailable.",
        );
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        setAuthenticatedChains([]);
        setAuthenticatedStatus("error");
        setAuthenticatedError(
          error?.message || "Authenticated option-chain data is unavailable.",
        );
      }
    }

    loadAuthenticatedChains();
    return () => {
      active = false;
      controller.abort();
    };
  }, [authenticatedOpportunities]);

  const scannerData = useMemo(
    () => buildAuthenticatedScannerData(authenticatedChains),
    [authenticatedChains],
  );
  const defaultSubmode = scannerData.results.some(
    (result) => result.submode === "same-day",
  )
    ? "same-day"
    : "longer-term";

  if (authenticatedStatus === "ready") {
    return (
      <NinaOptionsScanner
        snapshots={[]}
        evaluatedData={scannerData}
        defaultSubmode={defaultSubmode}
        defaultLimit="3"
        limitOptions={[3, 10, 25, 30, 50]}
        dataMode="authenticated-indicative"
        onOpenTradeReview={onOpenTradeReview}
      />
    );
  }

  return (
    <section
      className="optionsOpportunityEngine"
      aria-labelledby="options-engine-title"
    >
      <header className="optionsEngineHero">
        <div>
          <span>AUTHENTICATED ALPACA · INDICATIVE</span>
          <h1 id="options-engine-title">AI Options Engine</h1>
          <p>
            Loading authenticated option-chain evidence into the complete AI
            scanner layout. No order can be prepared, staged, or placed.
          </p>
        </div>
        <div
          className="optionsEngineStatus"
          aria-label="Options engine data status"
        >
          <strong>INDICATIVE</strong>
          <span>Authenticated indicative review</span>
        </div>
      </header>

      <div className="optionsEngineSafety" role="note">
        <strong>AUTHENTICATED INDICATIVE OPTIONS DATA — NON-EXECUTABLE.</strong>
        <span>
         Authenticated contract metadata is connected when available. Executable
OPRA data is not connected. Every contract remains WAIT, and no order is placed.
        </span>
      </div>

      <AuthenticatedOptionsStatus
        chains={authenticatedChains}
        error={authenticatedError}
        status={authenticatedStatus}
      />
    </section>
  );
}

function parseOptionContractSymbol(contractSymbol) {
  const normalizedSymbol = String(contractSymbol || "")
    .trim()
    .toUpperCase();
  const match = normalizedSymbol.match(
    /^(.+?)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/,
  );
  if (!match) return null;

  const [, underlying, year, month, day, typeCode, strikeCode] = match;
  const expiration = `20${year}-${month}-${day}`;
  const expirationDate = new Date(`${expiration}T00:00:00Z`);
  const today = new Date();
  const todayUtc = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const dte = Number.isNaN(expirationDate.getTime())
    ? null
    : Math.max(0, Math.ceil((expirationDate.getTime() - todayUtc) / 86400000));
  return {
    contractSymbol: normalizedSymbol,
    underlying,
    contractType: typeCode === "C" ? "call" : "put",
    strike: Number(strikeCode) / 1000,
    expiration,
    dte,
  };
}

function authenticatedOptionWaitReasons({
  bid,
  ask,
  spreadPercentage,
  volume,
  sourceTimestamp,
  sourceAgeMinutes,
  directionConfirmed,
  openInterest,
  contractMultiplier,
}) {
  const reasons = [];
  if (bid === null || ask === null) {
    reasons.push("Authenticated bid/ask is unavailable.");
  } else if (
    spreadPercentage === null ||
    spreadPercentage > MAX_AUTHENTICATED_OPTION_SPREAD_PERCENT
  ) {
    reasons.push(
      `Spread exceeds ${MAX_AUTHENTICATED_OPTION_SPREAD_PERCENT.toFixed(2)}%.`,
    );
  }
  if (volume === null) {
    reasons.push("Authenticated volume is unavailable.");
  } else if (volume < MIN_AUTHENTICATED_OPTION_VOLUME) {
    reasons.push(`Volume is below ${MIN_AUTHENTICATED_OPTION_VOLUME}.`);
  }
  if (sourceTimestamp === null || sourceAgeMinutes === null) {
    reasons.push("Authenticated source timestamp is unavailable.");
  } else if (sourceAgeMinutes > MAX_AUTHENTICATED_SOURCE_AGE_MINUTES) {
    reasons.push(`Source is stale at ${sourceAgeMinutes} minutes.`);
  }
  if (!directionConfirmed) reasons.push("Direction is unconfirmed.");
  if (openInterest === null) {
    reasons.push("Verified open interest is unavailable.");
  }
  if (contractMultiplier === null) {
    reasons.push("Verified contract multiplier is unavailable.");
  }
  reasons.push("Indicative option data is non-executable.");
  return reasons;
}

function buildAuthenticatedContractRows(chains) {
  return chains.flatMap((chain) =>
    Object.entries(chain.payload?.snapshots || {}).map(
      ([contractSymbol, snapshot]) => {
        const parsed = parseOptionContractSymbol(contractSymbol) || {
          contractSymbol,
          underlying: chain.opportunity?.symbol || null,
          contractType: null,
          strike: null,
          expiration: null,
          dte: null,
        };
        const quote = snapshot?.latestQuote || snapshot?.latest_quote || {};
        const trade = snapshot?.latestTrade || snapshot?.latest_trade || {};
        const dailyBar = snapshot?.dailyBar || snapshot?.daily_bar || {};
                const contractMetadata =
          snapshot?.contractMetadata || snapshot?.contract_metadata || {};
        const metadataSymbol = String(contractMetadata.symbol || "")
          .trim()
          .toUpperCase();
        const metadataUnderlying = String(
          contractMetadata.underlyingSymbol ||
            contractMetadata.underlying_symbol ||
            "",
        )
          .trim()
          .toUpperCase();
        const metadataMatchesContract =
          String(contractMetadata.provider || "").trim().toLowerCase() ===
            "alpaca" &&
          String(
            contractMetadata.sourceEndpoint ||
              contractMetadata.source_endpoint ||
              "",
          )
            .trim()
            .toLowerCase() === "option_contracts" &&
          metadataSymbol === String(parsed.contractSymbol || "").toUpperCase() &&
          metadataUnderlying === String(parsed.underlying || "").toUpperCase();

        const rawOpenInterest = metadataMatchesContract
          ? firstFiniteNumber(
              contractMetadata.openInterest,
              contractMetadata.open_interest,
            )
          : null;
        const openInterest =
          Number.isInteger(rawOpenInterest) && rawOpenInterest >= 0
            ? rawOpenInterest
            : null;

        const rawContractMultiplier = metadataMatchesContract
          ? firstFiniteNumber(
              contractMetadata.contractMultiplier,
              contractMetadata.contract_multiplier,
              contractMetadata.size,
            )
          : null;
        const contractMultiplier =
          Number.isInteger(rawContractMultiplier) && rawContractMultiplier > 0
            ? rawContractMultiplier
            : null;

        const rawOpenInterestDate = String(
          contractMetadata.openInterestDate ||
            contractMetadata.open_interest_date ||
            "",
        ).trim();
        const openInterestDate =
          metadataMatchesContract &&
          openInterest !== null &&
          /^\d{4}-\d{2}-\d{2}$/.test(rawOpenInterestDate)
            ? rawOpenInterestDate
            : null;
        const bid = firstFiniteNumber(quote.bp);
        const ask = firstFiniteNumber(quote.ap);
        const midpoint =
          bid !== null && ask !== null && ask >= bid ? (bid + ask) / 2 : null;
        const spreadPercentage =
          midpoint !== null && midpoint > 0
            ? ((ask - bid) / midpoint) * 100
            : null;
        const rawSourceTimestamp = quote.t || trade.t || dailyBar.t || null;
        const sourceTime = rawSourceTimestamp
          ? Date.parse(rawSourceTimestamp)
          : Number.NaN;
        const sourceTimestamp = Number.isNaN(sourceTime)
          ? null
          : new Date(sourceTime).toISOString();
        const sourceAgeMinutes = Number.isNaN(sourceTime)
          ? null
          : Math.max(0, Math.floor((Date.now() - sourceTime) / 60000));
        const greeks =
          snapshot?.greeks && typeof snapshot.greeks === "object"
            ? snapshot.greeks
            : null;
        const rawIv = firstFiniteNumber(
          snapshot?.impliedVolatility,
          snapshot?.implied_volatility,
        );
        const impliedVolatility =
          rawIv === null ? null : rawIv <= 5 ? rawIv * 100 : rawIv;
        const volume = firstFiniteNumber(dailyBar.v);
        const directionConfirmed = chain.directionConfirmed === true;
        const direction = !directionConfirmed
          ? "unconfirmed"
          : parsed.contractType === "put"
            ? "bearish"
            : parsed.contractType === "call"
              ? "bullish"
              : "unconfirmed";
        const waitReasons = authenticatedOptionWaitReasons({
          bid,
          ask,
          spreadPercentage,
          volume,
          sourceTimestamp,
          sourceAgeMinutes,
          directionConfirmed,
                    openInterest,
          contractMultiplier,
        });
        return {
          ...parsed,
          underlyingPrice: firstFiniteNumber(chain.opportunity?.price),
          direction,
          directionConfirmed,
          bid,
          ask,
          midpoint,
          spreadPercentage,
          volume,
          sourceTimestamp,
          sourceAgeMinutes,
          greeks,
          impliedVolatility,
          openInterest,
          openInterestDate,
          contractMultiplier,
          qualification: "WAIT",
          waitReasons,
        };
      },
    ),
  );
}

function compareAuthenticatedContracts(left, right) {
  const leftSpread = left.spreadPercentage ?? Number.POSITIVE_INFINITY;
  const rightSpread = right.spreadPercentage ?? Number.POSITIVE_INFINITY;
  const leftVolume = left.volume ?? -1;
  const rightVolume = right.volume ?? -1;
  const leftDistance =
    left.strike !== null && left.underlyingPrice
      ? Math.abs(left.strike - left.underlyingPrice) / left.underlyingPrice
      : Number.POSITIVE_INFINITY;
  const rightDistance =
    right.strike !== null && right.underlyingPrice
      ? Math.abs(right.strike - right.underlyingPrice) / right.underlyingPrice
      : Number.POSITIVE_INFINITY;
  return (
    Number(right.directionConfirmed) - Number(left.directionConfirmed) ||
    left.waitReasons.length - right.waitReasons.length ||
    leftSpread - rightSpread ||
    rightVolume - leftVolume ||
    leftDistance - rightDistance ||
    String(left.contractSymbol).localeCompare(String(right.contractSymbol))
  );
}

function prioritizeDifferentSymbols(contracts) {
  const representedSymbols = new Set();
  const symbolLeaders = [];
  const remainingContracts = [];

  contracts.forEach((contract) => {
    const symbol = String(
      contract.underlying || contract.contractSymbol || "",
    ).toUpperCase();
    if (!representedSymbols.has(symbol)) {
      representedSymbols.add(symbol);
      symbolLeaders.push(contract);
      return;
    }
    remainingContracts.push(contract);
  });

  return [...symbolLeaders, ...remainingContracts];
}

export function buildAuthenticatedScannerData(chains) {
  const rankedContracts = buildAuthenticatedContractRows(chains).sort(
    compareAuthenticatedContracts,
  );
  const contracts = [
    ...prioritizeDifferentSymbols(
      rankedContracts.filter(
        (contract) => contract.dte !== null && contract.dte <= 7,
      ),
    ),
    ...prioritizeDifferentSymbols(
      rankedContracts.filter(
        (contract) => contract.dte === null || contract.dte > 7,
      ),
    ),
  ];
  const results = contracts.map((contract, priorityGroup) => {
    const spread =
      contract.bid !== null &&
      contract.ask !== null &&
      contract.ask >= contract.bid
        ? Number((contract.ask - contract.bid).toFixed(4))
        : null;
    const breakeven =
      contract.strike !== null && contract.ask !== null
        ? contract.contractType === "call"
          ? contract.strike + contract.ask
          : contract.contractType === "put"
            ? contract.strike - contract.ask
            : null
        : null;
    const distanceToBreakevenPercent =
      breakeven !== null &&
      contract.underlyingPrice !== null &&
      contract.underlyingPrice > 0
        ? (Math.abs(breakeven - contract.underlyingPrice) /
            contract.underlyingPrice) *
          100
        : null;
    return {
      symbol: contract.underlying || contract.contractSymbol,
      contractId: contract.contractSymbol,
      dataMode: "authenticated-indicative",
      dataLabel: "AUTHENTICATED INDICATIVE",
      qualification: "WAIT",
      executable: false,
      direction: contract.direction,
      contractType: contract.contractType || "unavailable",
      setup: contract.directionConfirmed
        ? "WAIT — Authenticated direction confirmed"
        : "WAIT — Direction confirmation pending",
      expiration: contract.expiration,
      dte: contract.dte,
      strike: contract.strike,
      underlyingPrice: contract.underlyingPrice,
      bid: contract.bid,
      ask: contract.ask,
      spread,
      spreadPercent: contract.spreadPercentage,
      premium: null,
      maxLoss: null,
      volume: contract.volume,
      openInterest: contract.openInterest,
      openInterestDate: contract.openInterestDate,
      volumeOpenInterestRatio:
        contract.volume !== null &&
        contract.openInterest !== null &&
        contract.openInterest > 0
          ? contract.volume / contract.openInterest
          : null,
      impliedVolatility: contract.impliedVolatility,
      delta: firstFiniteNumber(contract.greeks?.delta),
      breakeven,
      distanceToBreakevenPercent,
      signals: {
        liquidity: { status: "unavailable", score: null },
        spreadQuality: { status: "unavailable", score: null },
      },
      catalyst: "Unavailable",
      catalystTiming: "Catalyst timing is unavailable.",
      scores: {
        suitability: null,
        smartFlow: null,
        confidence: null,
        completeness: null,
        factors: [],
      },
      state: "Insufficient evidence",
      riskLevel: "High",
      evidenceStatus: "unavailable",
      source: "Authenticated Alpaca indicative option chain",
      asOf: contract.sourceTimestamp || "Unavailable",
      updatedAt: contract.sourceTimestamp,
      stableKey: contract.contractSymbol,
      submode:
        contract.dte !== null && contract.dte <= 7 ? "same-day" : "longer-term",
      priorityGroup,
      blockReasons: contract.waitReasons,
      contractMultiplier: contract.contractMultiplier,
      occurrenceCount: 1,
      latestSeenAt: contract.sourceTimestamp || "Unavailable",
    };
  });
  return {
    results,
    errors: [],
    validCount: results.length,
    invalidCount: 0,
  };
}

function AuthenticatedOptionsStatus({ chains, error, status }) {
  const requestedSymbols = chains
    .map((chain) => chain.opportunity?.symbol)
    .filter(Boolean)
    .join(", ");
  if (status === "loading") {
    return (
      <p className="optionsEngineEmpty" role="status">
        Loading authenticated indicative option chains...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="optionsEngineEmpty" role="alert">
        {error || "Authenticated option-chain data is unavailable."}
      </p>
    );
  }
  if (status === "empty") {
    return (
      <p className="optionsEngineEmpty">
        Alpaca returned authenticated responses, but no matching option
        contracts were available for {requestedSymbols || "the current symbols"}
        . These symbols remain WAIT, and no options opportunity is presented.
      </p>
    );
  }
  return (
    <p className="optionsEngineEmpty">
      No authenticated option candidates are currently available from the
      Signature Scanner or dedicated options discovery.
    </p>
  );
}
